import type { ReactNode } from "react";
import { DEMO_AVISO, DEMO_VENDA } from "./conteudo";

/**
 * Celulares DESENHADOS em HTML/CSS para a landing — sem print de tela.
 *
 * Print envelhece a cada mudança de layout e carrega dado real (o print antigo
 * mostrava o nome da fazenda de origem). Desenho em marcação usa os tokens do
 * app, escala sem perder nitidez e o texto vem de `conteudo.ts`, com exemplo
 * fictício.
 *
 * Todo celular é `aria-hidden`: é ilustração. O que ele diz já está no texto da
 * seção, e um leitor de tela lendo "07:00 quinta-feira" no meio da página só atrapalha.
 */

export function MolduraCelular({ children, className = "" }: { children: ReactNode; className?: string }) {
  return (
    <div
      aria-hidden="true"
      className={`w-[240px] shrink-0 rounded-[2.25rem] border-[7px] border-foreground bg-background shadow-2xl overflow-hidden ${className}`}
    >
      <div className="h-6 flex items-center justify-center bg-inherit">
        <div className="h-1.5 w-16 rounded-full bg-foreground/80" />
      </div>
      {children}
    </div>
  );
}

/** O card de venda, como o produtor vê no app. */
export function CelularVenda({ className = "" }: { className?: string }) {
  const v = DEMO_VENDA;
  return (
    <MolduraCelular className={className}>
      <div className="px-4 pb-5 pt-1 space-y-3 text-foreground">
        <div className="flex items-center justify-between gap-2">
          <p className="font-semibold text-sm">{v.titulo}</p>
          <span className="rounded-full bg-primary/10 text-primary text-[10px] font-semibold px-2 py-0.5">
            {v.estado}
          </span>
        </div>
        <div className="text-[11px] text-muted-foreground leading-snug">
          <p>
            Comprador: <span className="text-foreground font-medium">{v.comprador}</span>
          </p>
          <p>{v.vendedor}</p>
        </div>
        <div className="rounded-xl border border-border divide-y divide-border">
          {v.linhas.map((l) => (
            <div key={l.descricao} className="px-3 py-2">
              <div className="flex justify-between text-xs font-medium">
                <span>{l.descricao}</span>
                <span>{l.valor}</span>
              </div>
              <p className="text-[10px] text-muted-foreground">{l.detalhe}</p>
            </div>
          ))}
          <div className="px-3 py-2 flex justify-between text-sm font-bold">
            <span>Total</span>
            <span>{v.total}</span>
          </div>
        </div>
        <p className="text-[11px] font-medium text-landing-lilas">{v.vencimento}</p>
        <div className="rounded-lg bg-primary text-primary-foreground text-xs font-semibold text-center py-2">
          {v.acao}
        </div>
      </div>
    </MolduraCelular>
  );
}

/** A tela de bloqueio com o aviso de vencimento — o que chega sem abrir o app. */
export function CelularAviso({ className = "" }: { className?: string }) {
  const a = DEMO_AVISO;
  return (
    <MolduraCelular className={`bg-landing-folha ${className}`}>
      <div className="px-3 pb-6 pt-3 text-white">
        <p className="text-center text-4xl font-semibold tracking-tight">{a.hora}</p>
        <p className="text-center text-[11px] text-white/75 mt-0.5 mb-5">{a.data}</p>
        <div className="space-y-2">
          <Notificacao app={a.app} quando={a.quando} titulo={a.titulo} texto={a.texto} />
          <Notificacao app={a.app} quando="ontem" titulo={a.segundo.titulo} texto={a.segundo.texto} apagada />
        </div>
      </div>
    </MolduraCelular>
  );
}

function Notificacao({
  app,
  quando,
  titulo,
  texto,
  apagada = false,
}: {
  app: string;
  quando: string;
  titulo: string;
  texto: string;
  apagada?: boolean;
}) {
  return (
    <div className={`rounded-2xl bg-background/95 text-foreground px-3 py-2.5 shadow ${apagada ? "opacity-75" : ""}`}>
      <div className="flex items-center gap-1.5 text-[10px] text-muted-foreground mb-0.5">
        <span className="h-3.5 w-3.5 rounded bg-landing-folha inline-block" />
        <span className="font-medium uppercase tracking-wide">{app}</span>
        <span className="ml-auto">{quando}</span>
      </div>
      <p className="text-xs font-semibold leading-snug">{titulo}</p>
      <p className="text-[11px] text-muted-foreground leading-snug">{texto}</p>
    </div>
  );
}

/** Uma conversa de WhatsApp com UMA mensagem do Rizoma (seção "No WhatsApp"). */
export function CelularConversa({ linhas, className = "" }: { linhas: readonly string[]; className?: string }) {
  return (
    <MolduraCelular className={className}>
      <div className="flex items-center gap-2 px-3 py-2 bg-landing-folha text-white">
        <span className="h-6 w-6 rounded-full bg-white/20 inline-block" />
        <div className="leading-tight">
          <p className="text-xs font-semibold">Rizoma</p>
          <p className="text-[10px] text-white/75">conta comercial</p>
        </div>
      </div>
      <div className="bg-landing-fundo px-3 py-4 min-h-[180px]">
        <div className="rounded-xl rounded-tl-sm bg-background shadow-sm px-3 py-2 text-[12px] leading-relaxed text-foreground max-w-[95%]">
          {linhas.map((l, i) => (
            <p key={i} className={i === 0 ? "font-semibold" : ""}>
              {l}
            </p>
          ))}
          <p className="text-[9px] text-muted-foreground text-right mt-1">07:00</p>
        </div>
      </div>
    </MolduraCelular>
  );
}
